// safetyPage/components/nearMiss/NearMissTab.js
// This will hold the NearMissTable and the NearMissForm for users to fill out.

import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  parseSafetyFormRoute,
  buildCanonicalSafetyFormPath,
} from "../../../../services/SafetyFormRouteUtils";
import { shallow } from "zustand/shallow";
import { useAppStore } from "../../../../store/appStore";
// User
import { useUserState } from "../../../../context/user";
// Components
import { NearMissTable } from "./NearMissTable";
import { NearMissForm } from "../../../nearMissForm/NearMissForm";
import {
  NewSafetyFormButton,
  EditSafetyFormButton,
  RefreshSafetyFormListsButton,
} from "../../../landingPage/components/buttons/SafetyFormActions";
// Styles
import styles from "./NearMissTab.module.css";

export const NearMissTab = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // User Info
  const user = useUserState().currentUser;

  // Employees
  const employees = useAppStore((s) => s.employees.list, shallow);

  // Loading UI actions
  const showLoading = useAppStore((s) => s.loadingActions.showLoading);
  const hideLoading = useAppStore((s) => s.loadingActions.hideLoading);

  // Notification UI actions
  const { showNotification } = useAppStore(
    (s) => s.notificationActions,
    shallow
  );

  // Near Miss List State/Actions
  const nearMissList = useAppStore(
    (s) => s.safetyFormLists.nearMissList.items,
    shallow
  );
  const totalCount = useAppStore(
    (s) => s.safetyFormLists.nearMissList.totalCount,
    shallow
  );
  const currentPage = useAppStore(
    (s) => s.safetyFormLists.nearMissList.currentPage,
    shallow
  );
  const pageSize = useAppStore(
    (s) => s.safetyFormLists.nearMissList.pageSize,
    shallow
  );
  const isLoading = useAppStore(
    (s) => s.safetyFormLists.nearMissList.loading,
    shallow
  );

  // Near Miss List Actions
  const setNearMissListPage = useAppStore(
    (s) => s.safetyFormListsActions.setNearMissListPage
  );
  const setNearMissListPageSize = useAppStore(
    (s) => s.safetyFormListsActions.setNearMissListPageSize
  );
  const fetchNearMissListDraftsPage = useAppStore(
    (s) => s.safetyFormListsActions.fetchNearMissListDraftsPage
  );

  // Near Miss Form UI/State
  const showNearMissForm = useAppStore(
    (s) => s.safetyPage.showNearMissForm,
    shallow
  );

  const setShowNearMissForm = useAppStore(
    (s) => s.safetyPageActions.setShowNearMissForm
  );

  // Selected Near Miss from table State/Actions
  const selectedNearMiss = useAppStore(
    (s) => s.safetyPage.selectedNearMiss,
    shallow
  );
  const setSelectedNearMiss = useAppStore(
    (s) => s.safetyPageActions.setSelectedNearMiss
  );

  // Near Miss Form stuff
  // State
  const activeNearMiss = useAppStore((s) => s.activeNearMiss, shallow);
  const activeNearMissFormActions = useAppStore(
    (s) => s.activeNearMissActions.nearMissFormActions,
    shallow
  );
  // Actions
  const safetyFormWorkflowActions = useAppStore(
    (s) => s.safetyFormWorkflowActions,
    shallow
  );
  const startNewNearMiss = useAppStore(
    (s) => s.activeNearMissActions.startNewNearMiss
  );
  const fetchNearMiss = useAppStore(
    (s) => s.activeNearMissActions.fetchNearMiss
  );
  const setNearMissErrors = useAppStore(
    (s) => s.activeNearMissActions.setNearMissErrors
  );

  // Handling row select
  const handleRowSelect = (nearMiss) => {
    setSelectedNearMiss(nearMiss);
  };

  // Handling Double clicking a row
  const handleDoubleClick = async (nearMiss) => {
    setSelectedNearMiss(nearMiss);
    const target = buildCanonicalSafetyFormPath({
      tab: "near_miss",
      nearMissId: nearMiss.id,
      action: "edit",
      open: true,
    });
    navigate(target);
  };

  // Handling delete
  const handleDelete = async (nearMissId) => {
    showLoading("Deleting near miss...");
    const res = await safetyFormWorkflowActions.deleteNearMiss(
      nearMissId,
      user
    );
    if (res?.ok) {
      showNotification({
        type: "success",
        title: "Near Miss Deleted",
        message: "The near miss has been deleted successfully.",
      });
    } else {
      showNotification({
        type: "error",
        title: "Error Deleting Near Miss",
        message: "The near miss has not been deleted. Please try again.",
      });
    }
    hideLoading();
  };

  // Handle creating new near miss when button is clicked
  const handleNewNearMiss = async () => {
    const target = buildCanonicalSafetyFormPath({
      tab: "near_miss",
      action: "new",
      open: true,
    });
    navigate(target);
  };

  // Handle edit button click
  const handleEditNearMiss = () => {
    if (!selectedNearMiss) return;
    const target = buildCanonicalSafetyFormPath({
      tab: "near_miss",
      nearMissId: selectedNearMiss.id,
      action: "edit",
      open: true,
    });
    navigate(target);
  };

  // Pagination handlers
  const handlePageSizeChange = async (e) => {
    showLoading("Loading Near Misses...");
    const newPageSize = parseInt(e.target.value, 10);
    setNearMissListPageSize(newPageSize);
    await fetchNearMissListDraftsPage(user.employee_id);
    hideLoading();
  };

  const handlePageChange = async (direction) => {
    showLoading("Loading Near Misses...");
    let newPage;
    if (direction === "next") {
      newPage = currentPage + 1;
    } else {
      newPage = currentPage - 1;
    }
    setNearMissListPage({ currentPage: newPage, pageSize });
    await fetchNearMissListDraftsPage(user.employee_id);
    hideLoading();
  };

  // Refresh handler
  const handleRefreshNearMissLists = async () => {
    showLoading("Refreshing Near Misses...");
    setNearMissListPage({ currentPage: 1, pageSize });
    await fetchNearMissListDraftsPage(user.employee_id);
    hideLoading();
  };

  // Deep-link handler (single source of truth)
  const processedRef = useRef(null);
  useEffect(() => {
    const run = async () => {
      const parsed = parseSafetyFormRoute(location.pathname, location.search);
      if (parsed.tab !== "near_miss") {
        return;
      }

      const idStr = parsed.nearMissId;
      const action = parsed.action || (idStr ? "edit" : null);
      const wantsOpen = !!parsed.open;
      const key = `near_miss:${action || ""}:${idStr || ""}:${
        wantsOpen ? "1" : "0"
      }`;

      if (processedRef.current === key) {
        return;
      }
      processedRef.current = key;

      try {
        // If URL points to list view (no near_miss_id, no action), ensure form is closed
        if (!idStr && !action) {
          if (showNearMissForm) setShowNearMissForm(false);
          return;
        }

        // Guard: If already open on the same near miss in edit mode, skip re-fetch
        if (
          idStr &&
          wantsOpen &&
          action === "edit" &&
          showNearMissForm &&
          activeNearMiss?.currentNearMiss?.id &&
          String(activeNearMiss.currentNearMiss.id) === String(idStr)
        ) {
          return;
        }

        if (action === "new" && wantsOpen) {
          showLoading("Starting new Near Miss...");
          await startNewNearMiss(user);
          setShowNearMissForm(true);

          // After creating new Near Miss, update URL to edit mode with near_miss_id
          // This prevents refresh from creating another new Near Miss
          const newNearMissId =
            useAppStore.getState().activeNearMiss.currentNearMiss?.id;
          if (newNearMissId) {
            const editTarget = buildCanonicalSafetyFormPath({
              tab: "near_miss",
              nearMissId: newNearMissId,
              action: "edit",
              open: true,
            });
            navigate(editTarget, { replace: true });
          }
        } else if (idStr && wantsOpen && action === "edit") {
          showLoading("Loading Near Miss...");
          const res = await fetchNearMiss("edit", idStr);
          if (res && res.ok) {
            setShowNearMissForm(true);
          } else {
            setShowNearMissForm(false);
            showNotification({
              type: "error",
              title: "Error Loading Near Miss",
              message: "Near Miss not found or could not be loaded.",
            });
          }
        } else if (idStr && !wantsOpen) {
          // Select the row but don't open the form
          const nearMiss = nearMissList.find(
            (nm) => String(nm.id) === String(idStr)
          );
          if (nearMiss) setSelectedNearMiss(nearMiss);
          setShowNearMissForm(false);
        }
      } catch (err) {
        console.error("Failed to process Near Miss deep link", err);
      } finally {
        hideLoading();
      }
    };
    run();
  }, [
    location.pathname,
    location.search,
    startNewNearMiss,
    fetchNearMiss,
    setShowNearMissForm,
    showLoading,
    hideLoading,
    user,
    nearMissList,
    activeNearMiss,
    showNearMissForm,
    showNotification,
    setSelectedNearMiss,
  ]);

  return (
    <>
      {showNearMissForm ? (
        <>
          <NearMissForm
            currentNearMiss={activeNearMiss.currentNearMiss}
            nearMissMode={activeNearMiss.nearMissMode}
            nearMissFormUI={activeNearMiss.nearMissForm?.nearMissFormUI}
            photosUI={activeNearMiss.nearMissForm?.photosUI}
            rev={activeNearMiss.rev}
            nearMissFormActions={activeNearMissFormActions}
            setShowNearMissForm={setShowNearMissForm}
            autosaveErrors={activeNearMiss.nearMissForm?.autosaveErrors}
            autosaveErrorsActions={activeNearMissFormActions?.autosaveErrors}
            refreshListFunction={handleRefreshNearMissLists}
          />
        </>
      ) : (
        <>
          <div className={styles.nearMissTabContainer}>
            <div className={styles.tableActions}>
              <NewSafetyFormButton
                formName="Near Miss"
                newFormFunction={handleNewNearMiss}
              />
              <EditSafetyFormButton
                formName="Near Miss"
                editFunction={handleEditNearMiss}
              />
            </div>

            <div className={styles.headerWithRefresh}>
              <h2>Near Miss Forms</h2>
              <RefreshSafetyFormListsButton
                refreshFunction={handleRefreshNearMissLists}
                isLoading={isLoading}
              />
            </div>

            <div className={styles.nearMissTableWrapper}>
              {nearMissList?.length > 0 ? (
                <NearMissTable
                  nearMissList={nearMissList.slice(0, pageSize)}
                  onRowSelect={handleRowSelect}
                  onDoubleClick={handleDoubleClick}
                  onDelete={handleDelete}
                  selectedNearMissId={selectedNearMiss?.id}
                />
              ) : (
                <div className={styles.noData}>No near misses to display</div>
              )}
            </div>
            <div className={styles.paginationControls}>
              <div className={styles.paginationLeftControls}>
                <button
                  onClick={() => handlePageChange("prev")}
                  disabled={currentPage === 1 || isLoading}
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange("next")}
                  disabled={currentPage * pageSize >= totalCount || isLoading}
                >
                  Next
                </button>
                <div className={styles.pageSizeSelector}>
                  <label htmlFor="pageSize">Rows:</label>
                  <select
                    id="pageSize"
                    className={styles.pageSizeSelect}
                    value={pageSize}
                    onChange={handlePageSizeChange}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                  </select>
                </div>
              </div>
              <span>
                Showing {Math.min((currentPage - 1) * pageSize + 1, totalCount)}{" "}
                - {Math.min(currentPage * pageSize, totalCount)} of {totalCount}{" "}
                near misses
              </span>
            </div>
          </div>
        </>
      )}
    </>
  );
};
